import React, { SetStateAction, useCallback, useState } from "react"
import { Box, Flex } from "@chakra-ui/react"
import { Route, useHistory } from "react-router"
import { useForm } from "queries/forms"
import Spinner from "components/elements/Spinner"
import { useQueryClient } from "react-query"
import { refetchEventQuestions } from "context/actions"
import { eventEditFormPath } from "utilities/routes"
import {
  BuilderBlockWithBlockId, FormQuestion, DioboxEvent, ModalName,
} from "sharedTypes"
import { handleAddBlock, handleBlockContentChange, handleDeleteBlock } from "components/Builder/helpers"
import { BuilderBlock, BuilderBlockType, DraggableItem } from "sharedTypes/builder"
import { postEventQuestion } from "api/forms"
import { getEnabledFormTypesBySubscriptionLevel } from "utilities/forms"
import useCurrentAccount from "services/useCurrentAccount"
import { BlockInsertPosition } from "components/Builder/BuildingBlock"
import useModal from "services/useModal"
import { useLocation } from "react-router-dom"
import { useDeepCompareEffect } from "ahooks"
import StatusBox from "./StatusBox/StatusBox"
import FormContainer from "./FormContainer/FormContainer"
import EditForm from "./EditForm/EditForm"
import FormUpgradeBox from "./FormUpgradeBox"
import FormOptions from "./FormOptions"

type Props = {
  formId: string
  event: DioboxEvent
  formsCount: number
}

const SelectedForm = ({
  formId, event, formsCount,
}: Props) => {
  const queryClient = useQueryClient()
  const history = useHistory()
  const { id: eventId } = event
  const { isLoading, data: currentForm } = useForm(eventId, formId)
  const [selectedBlockId, setSelectedBlockId] = useState<number>()
  const { subscription } = useCurrentAccount()
  const showModal = useModal()
  const location = useLocation()
  const isEditMode = location.pathname.endsWith("edit")

  const [blocks, _setBlocks] = useState<BuilderBlockWithBlockId[] | undefined>()
  const setBlocks = (newBlocks: SetStateAction<BuilderBlockWithBlockId[]>) => {
    if (typeof newBlocks === "function") {
      _setBlocks((prev) => newBlocks(prev ?? []))
    } else {
      _setBlocks(newBlocks)
    }
  }

  useDeepCompareEffect(() => {
    if (!currentForm) {
      return
    }

    // Only set blocks if not in edit mode or in edit mode but no blocks yet (refreshed edit page)
    if (isEditMode && blocks !== undefined) {
      return
    }

    setBlocks(currentForm.formData.blocks.map(
      (block, index) => (
        { block, blockId: index } as BuilderBlockWithBlockId),
    ))
  }, [currentForm, isEditMode])

  const handleAddFormQuestionBlock = (
    blockToAdd: BuilderBlock,
    position: BlockInsertPosition,
    blockId?: number,
  ) => {
    handleAddBlock({
      type: BuilderBlockType.QUESTION,
      setBlocks,
      setSelectedBlockId,
      position,
      blockId,
      block: blockToAdd,
    })
  }

  const handleDropBlock = useCallback((
    item: DraggableItem,
    blockId: number,
    position: BlockInsertPosition,
  ) => {
    if (item.blockTypeToAdd === BuilderBlockType.QUESTION) {
      showModal(
        ModalName.AddFormQuestion, {
          blocks,
          addBlock: handleAddFormQuestionBlock,
          selectedQuestionId: blockId,
          position,
        },
      )
    } else if (item.blockTypeToAdd) {
      handleAddBlock({
        type: item.blockTypeToAdd,
        setBlocks,
        setSelectedBlockId,
        position,
        blockId,
      })
    }
  }, [blocks, setBlocks, handleAddFormQuestionBlock, showModal, handleAddBlock, setSelectedBlockId])

  if (!blocks) {
    return null
  }

  const handleDuplicateBlock = async (blockId: number) => {
    const blockToDuplicate = blocks.find((block) => block.blockId === blockId)

    if (!blockToDuplicate) {
      return
    }

    let blockDataToDuplicate = blockToDuplicate.block

    if (blockToDuplicate.block.type === BuilderBlockType.QUESTION) {
      await postEventQuestion(event.id, blockToDuplicate.block.data as FormQuestion)
        .then(async (data) => {
          blockDataToDuplicate = { type: BuilderBlockType.QUESTION, data: data.data }
          await refetchEventQuestions(queryClient, event.id)
        })
    }

    handleAddBlock({
      type: blockToDuplicate.block.type,
      setBlocks,
      setSelectedBlockId,
      position: BlockInsertPosition.Bottom,
      blockId,
      block: blockDataToDuplicate,
    })
  }

  const handleEditForm = () => {
    setSelectedBlockId(undefined)
    history.push(eventEditFormPath(event.id, formId))
  }

  if (isLoading) {
    return <Spinner heightFull />
  }

  if (!currentForm) {
    return null
  }

  const enabledFormTypes = getEnabledFormTypesBySubscriptionLevel(subscription)

  const formTypeDisabled = !enabledFormTypes.includes(currentForm.type)

  return (
    <>
      <Flex flexDir="column" alignItems="flex-end" gap={6}>
        <FormOptions
          onClickEditForm={handleEditForm}
          form={currentForm}
          formsCount={formsCount}
          formTypeDisabled={formTypeDisabled}
          event={event}
        />
        {formTypeDisabled ? <FormUpgradeBox formType={currentForm.type} /> : (
          <StatusBox form={currentForm} event={event} />
        )}
      </Flex>
      <Box pt={8} pb={10} w={480} margin="auto">
        <FormContainer
          form={currentForm}
          blocks={blocks}
          setBlocks={setBlocks}
          onDeleteBlock={() => handleDeleteBlock(setBlocks, selectedBlockId)}
          onDuplicateBlock={handleDuplicateBlock}
          portalPrimaryColor={event.portalPrimaryColor}
          mergeTagEventTitle={event.publicEventTitle}
        />
      </Box>
      <Route path={`${eventEditFormPath(":eventId", ":formId")}`}>
        <EditForm
          blocks={blocks}
          onBlockContentChange={
            (data) => handleBlockContentChange(data, setBlocks, selectedBlockId)
          }
          selectedBlockId={selectedBlockId}
          setSelectedBlockId={setSelectedBlockId}
          setBlocks={setBlocks}
          onDeleteBlock={() => handleDeleteBlock(setBlocks, selectedBlockId)}
          onDuplicateBlock={handleDuplicateBlock}
          event={event}
          onDropBlock={handleDropBlock}
        />
      </Route>
    </>
  )
}

export default SelectedForm
